import "./styles/app.css";
import lightCssUrl from "github-markdown-css/github-markdown-light.css?url";
import darkCssUrl from "github-markdown-css/github-markdown-dark.css?url";
import { SignalingClient } from "./signal";
import { connectPeer } from "./peer";
import { Transport } from "./transport";
import { loadInitial } from "./nav";
import { rehighlightAll } from "./renderer/highlight";

type Theme = "light" | "dark";

function setupTheme(): void {
  const lightLink = document.createElement("link");
  lightLink.rel = "stylesheet";
  lightLink.href = lightCssUrl;
  const darkLink = document.createElement("link");
  darkLink.rel = "stylesheet";
  darkLink.href = darkCssUrl;
  document.head.append(lightLink, darkLink);

  const btn = document.getElementById("theme-btn") as HTMLButtonElement | null;
  const apply = (theme: Theme): void => {
    document.documentElement.dataset.theme = theme;
    lightLink.disabled = theme !== "light";
    darkLink.disabled = theme !== "dark";
    if (btn) btn.textContent = theme === "dark" ? "☀" : "\u{1F319}";
    window.dispatchEvent(new CustomEvent("mdview:theme", { detail: theme }));
  };

  const initial: Theme =
    document.documentElement.dataset.theme === "dark" ? "dark" : "light";
  apply(initial);

  btn?.addEventListener("click", () => {
    const next: Theme =
      document.documentElement.dataset.theme === "dark" ? "light" : "dark";
    try { localStorage.setItem("mdview-theme", next); } catch { /* */ }
    apply(next);
  });

  window.addEventListener("mdview:theme", () => {
    const root = document.getElementById("root");
    if (root) void rehighlightAll(root);
  });
}

setupTheme();

interface HashState {
  room: string;
  token: string;
}

function parseHash(): HashState | null {
  const raw = location.hash.replace(/^#/, "");
  if (!raw) return null;
  const params = new URLSearchParams(raw);
  const room = params.get("r");
  const token = params.get("t");
  if (!room || !token) return null;
  if (!/^[A-Za-z0-9_-]{12}$/.test(room)) return null;
  return { room, token };
}

function signalingUrl(room: string, token: string): string {
  const base = location.host;
  const proto = location.protocol === "https:" ? "wss:" : "ws:";
  return `${proto}//${base}/api/signal/${room}?role=viewer&t=${encodeURIComponent(token)}`;
}

function setStatus(state: "disconnected" | "connecting" | "connected" | "error", label?: string): void {
  const el = document.getElementById("status");
  if (!el) return;
  el.dataset.state = state;
  el.textContent = label ?? state;
}

function toast(msg: string, kind: "info" | "error" = "info"): void {
  const el = document.getElementById("toast");
  if (!el) return;
  el.textContent = msg;
  el.dataset.kind = kind;
  el.hidden = false;
  window.setTimeout(() => { el.hidden = true; }, 4000);
}

document.getElementById("print-btn")?.addEventListener("click", () => window.print());

async function start(): Promise<void> {
  const state = parseHash();
  if (!state) {
    setStatus("disconnected", "no room");
    return;
  }
  setStatus("connecting");

  let signal: SignalingClient;
  try {
    signal = new SignalingClient(signalingUrl(state.room, state.token));
    await signal.ready;
  } catch (err) {
    setStatus("error", "signaling failed");
    toast(`Signaling error: ${(err as Error).message}`, "error");
    return;
  }

  // Clear the join token from the URL so a refresh doesn't try to reuse it.
  history.replaceState(null, "", `${location.pathname}#r=${state.room}`);

  const peer = connectPeer(signal);

  // First message we expect from CLI after the peer-joined notification:
  //   { type:"init", entry:"/abs/path/to/file.md" }
  // delivered on the data channel.
  const dc = await peer.channel;
  setStatus("connected");

  // Tell the host we're ready so it can close signaling.
  signal.send({ type: "ready" });
  // The Worker will then schedule a 30s alarm and tear the room down.
  setTimeout(() => signal.close(), 100);

  const transport = new Transport(dc);
  const root = document.getElementById("root") as HTMLElement;

  // The first ctrl message on the data channel from the CLI tells us the
  // initial document path.
  const initPath = await new Promise<string>((resolve) => {
    transport.onPush((m) => {
      if (m.type === "init" && typeof m.path === "string") resolve(m.path);
    });
    transport.sendControl({ type: "viewer-ready" });
  });

  try {
    const nav = await loadInitial({ transport, root }, initPath);
    transport.onPush((m) => {
      if (m.type === "changed" && typeof m.path === "string") {
        nav.reloadIfAffected(m.path);
      }
    });
  } catch (err) {
    toast(`Render error: ${(err as Error).message}`, "error");
  }

  peer.closed.then(() => setStatus("disconnected"));
}

window.addEventListener("hashchange", () => {
  // A new room code arrived in the URL — reload the page to start fresh.
  location.reload();
});

void start();
